import { Button, Modal } from "react-bootstrap";
import SpinnerButton from "../../../../components/SpinnerButton";
import ConditionalSpinner from "../../../../components/ConditionalSpinner";
import { useState, ChangeEvent, useEffect } from "react";
import { Project } from "../../../../models/Project";
import Select from "react-select";
import { User, PermissionAction, Permission, UserGroup } from "../../../../models/User";
import { Notifications } from "../../../../utilities/Notifications/Notifications";
import {
    useGetUsersQuery,
    useAddUserPermissionMutation,
    useGetProjectPermissionsQuery,
    useGetUserGroupsQuery,
} from "../../../../store/api/kinesense";

const PERMISSION_ACTIONS = ["peek", "read", "update", "manage", "delete"] as const;
const DELAY_BETWEEN_CALLS = 100; // milliseconds

const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === "object" && "data" in error) {
        const rtqError = error as { data?: { message?: string } };
        if (rtqError.data?.message) return rtqError.data.message;
    }
    if (error instanceof Error) return error.message;
    return "Unknown error";
};

export interface QuickAddProjectMembersModalProps {
    isVisible?: boolean;
    project?: Project;
    setProject: (project: Project | null) => void;
}

export function QuickAddProjectMembersModal(props: QuickAddProjectMembersModalProps) {
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userPermissions, setUserPermissions] = useState<PermissionAction[]>([...PERMISSION_ACTIONS]);
    const [existingMembers, setExistingMembers] = useState<{ user: User; permissions: Permission }[]>([]);
    const [existingGroups, setExistingGroups] = useState<{ group: UserGroup; permissions: Permission }[]>([]);

    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery({});
    const { data: userGroups, isLoading: isLoadingGroups } = useGetUserGroupsQuery();
    const { data: projectPermissions, isLoading: isLoadingPermissions } = useGetProjectPermissionsQuery(
        { projectId: props.project?.projectId || "" },
        { skip: !props.project?.projectId },
    );
    const [addUserPermission] = useAddUserPermissionMutation();

    useEffect(() => {
        if (projectPermissions && users) {
            const userPerms = projectPermissions.filter(
                (perm) => perm.target.type === "user" && perm.subject === "projects",
            );
            const members = userPerms
                .map((perm) => {
                    const user = users.find((u) => u.username === perm.target.targetId);
                    return user ? { user, permissions: perm } : null;
                })
                .filter((item) => item !== null) as { user: User; permissions: Permission }[];
            setExistingMembers(members);
        }
    }, [projectPermissions, users]);

    useEffect(() => {
        if (projectPermissions && userGroups) {
            const groupPerms = projectPermissions.filter(
                (perm) => perm.target.type === "group" && perm.subject === "projects",
            );
            const groups = groupPerms
                .map((perm) => {
                    const group = userGroups.find((g) => g.groupId === perm.target.targetId);
                    return group ? { group, permissions: perm } : null;
                })
                .filter((item) => item !== null) as { group: UserGroup; permissions: Permission }[];
            setExistingGroups(groups);
        }
    }, [projectPermissions, userGroups]);

    const hideModal = () => {
        props.setProject(null);
        setSelectedUser(null);
        setUserPermissions([...PERMISSION_ACTIONS]);
    };

    const isValid = selectedUser !== null && userPermissions.length > 0;

    const handleUserChange = (selectedOption: { value: string; label: string } | null) => {
        if (selectedOption && users) {
            const user = users.find((u) => u.username === selectedOption.value);
            setSelectedUser(user || null);
        } else {
            setSelectedUser(null);
        }
    };

    const handlePermissionChange = (input: ChangeEvent<HTMLInputElement>, action: PermissionAction) => {
        const isChecked = input.target.checked;
        setUserPermissions((prev) => {
            const perms = [...prev];

            if (isChecked) {
                perms.push(action);

                if (action == "read" && !perms.includes("peek")) {
                    perms.push("peek");
                }

                if ((action == "update" || action == "delete") && !perms.includes("read")) {
                    perms.push("read");
                    perms.push("peek");
                }
            } else {
                const index = perms.indexOf(action);
                if (index > -1) {
                    perms.splice(index, 1);
                }
            }

            return perms;
        });
    };

    const savePermissions = async () => {
        if (!selectedUser || !props.project) return;

        setIsSavingPermissions(true);
        try {
            await addUserPermission({
                username: selectedUser.username,
                permission: {
                    type: "project",
                    projectId: props.project.projectId,
                    actions: userPermissions,
                    subject: "projects",
                },
            }).unwrap();

            Notifications.notify(
                "Success",
                `User ${selectedUser.username} has been added to the project ${props.project.name}`,
            );
            hideModal();
        } catch (error) {
            Notifications.notify("Error", `Failed to add user: ${getErrorMessage(error)}`);
        } finally {
            setIsSavingPermissions(false);
        }
    };

    return (
        <Modal show={props.isVisible} centered backdrop="static" size="lg">
            <Modal.Header closeButton onHide={hideModal}>
                <Modal.Title>Add User To Project</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {existingMembers.length > 0 && (
                    <div style={{ marginBottom: "2rem" }}>
                        <h5 style={{ marginBottom: "1rem" }}>Current Users</h5>
                        <ConditionalSpinner size="sm" isLoading={isLoadingPermissions}>
                            <table style={{ width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "40%" }}>User</th>
                                        <th style={{ width: "60%" }}>Permissions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingMembers.map(({ user, permissions }) => (
                                        <tr key={user.username}>
                                            <td>{user.username}</td>
                                            <td>
                                                {permissions.actions
                                                    .map((action) => action.charAt(0).toUpperCase() + action.slice(1))
                                                    .join(", ")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ConditionalSpinner>
                    </div>
                )}

                {existingGroups.length > 0 && (
                    <div style={{ marginBottom: "2rem" }}>
                        <h5 style={{ marginBottom: "1rem" }}>Current Groups</h5>
                        <ConditionalSpinner size="sm" isLoading={isLoadingPermissions}>
                            <table style={{ width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "40%" }}>Group Name</th>
                                        <th style={{ width: "60%" }}>Permissions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingGroups.map(({ group, permissions }) => (
                                        <tr key={group.groupId}>
                                            <td>{group.name}</td>
                                            <td>
                                                {permissions.actions
                                                    .map((action) => action.charAt(0).toUpperCase() + action.slice(1))
                                                    .join(", ")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ConditionalSpinner>
                    </div>
                )}

                <h5 style={{ marginBottom: "1rem" }}>Add New User</h5>
                <div style={{ marginBottom: "1.5rem" }}>
                    <Select
                        isDisabled={isLoadingUsers || isSavingPermissions}
                        value={selectedUser ? { value: selectedUser.username, label: selectedUser.username } : null}
                        options={
                            users
                                ?.filter(
                                    (user) => !existingMembers.some((member) => member.user.username === user.username),
                                )
                                .map((user) => ({
                                    value: user.username,
                                    label: user.username,
                                })) || []
                        }
                        classNamePrefix="react-select"
                        placeholder="Select a user..."
                        onChange={handleUserChange}
                        isClearable
                    />
                </div>

                {selectedUser && (
                    <div>
                        <label style={{ marginBottom: "0.5rem", fontWeight: 600 }}>Permissions</label>
                        <ConditionalSpinner size="sm" isLoading={false}>
                            <table style={{ width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "30%" }}>Permission</th>
                                        <th style={{ textAlign: "center", padding: "0 0.5rem" }}>Granted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PERMISSION_ACTIONS.map((action) => (
                                        <tr key={action}>
                                            <td style={{ textTransform: "capitalize" }}>{action}</td>
                                            <td style={{ textAlign: "center", padding: "0 0.5rem" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={userPermissions.includes(action)}
                                                    onChange={(e) => handlePermissionChange(e, action)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ConditionalSpinner>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={hideModal} disabled={isSavingPermissions}>
                    Cancel
                </Button>
                <SpinnerButton
                    isLoading={isSavingPermissions}
                    label="Add User"
                    buttonProps={{
                        type: "button",
                        variant: "success",
                        disabled: !isValid || isSavingPermissions,
                        onClick: savePermissions,
                    }}
                />
            </Modal.Footer>
        </Modal>
    );
}
